//@ts-nocheck
import { generateClient } from "aws-amplify/api";
import { Permission } from "db/definitions";
import {
	createPermission as createLocalPerm,
	deletePerm as deleteLocalPerm,
	fetchPermissions,
	putPermission as updateLocalPerm,
} from "db/queries/permissionQueries";
import { useEffect, useState } from "react";
import { getPermissions } from "utils/getPermissions";
import { createPermission as createPerm, updatePermission as updatePerm, deletePermission as deletePerm } from "graphql/mutations";
import { recordSentry } from "utils/recordSentry";

interface Arguments {
	/**
	 * Whether or not the user is online
	 */
	online: boolean;
}

function usePermission(args: Arguments) {
	const { online } = args;
	const [permissions, setPermissions] = useState<Permission[]>([]);

	useEffect(() => {
		async function fetchPerms() {
			if (online) {
				const response = await getPermissions();
				setPermissions(response);
			} else {
				const response = await fetchPermissions();
				setPermissions(response);
			}
		}
		fetchPerms();
	}, [online]);

	/**
	 * Creates a new permission, setting its timestamps and syncing it with the server or local storage as needed.
	 * Updates the local permissions state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param permission - The permission object to be created.
	 * @returns A promise that resolves when the permission is created.
	 */
	async function createPermission(permission: Permission): Promise<void> {
		const timestamp = new Date().toISOString();
		permission.createdAt = timestamp;
		permission.updatedAt = timestamp;
		try {
			if (online) {
				try {
					const client = generateClient({ authMode: "userPool" });
					await client.graphql({
						query: createPerm,
						authMode: "userPool",
						variables: {
							input: permission,
						},
					});
				} catch (error) {
					console.log(error);
				}
			} else {
				await createLocalPerm(permission);
			}
			const newPermissions = [...permissions, permission];
			setPermissions(newPermissions);
		} catch (err) {
			recordSentry({ error: err, msg: "Failed to create permission " + JSON.stringify(permission) });
		}
	}

	/**
	 * Updates an existing permission, syncing changes with the server or local storage as needed.
	 * Updates the local permissions state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param permission - The permission object to be updated.
	 * @returns A promise that resolves when the permission is updated.
	 */
	async function updatePermission(permission: Permission): Promise<void> {
		permission.updatedAt = new Date().toISOString();
		try {
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				await client.graphql({
					query: updatePerm,
					authMode: "userPool",
					variables: {
						input: permission,
					},
				});
			} else {
				await updateLocalPerm(permission);
			}
			const newPermissions = permissions.map((perm) => (perm.id === permission.id ? permission : perm));
			setPermissions(newPermissions);
		} catch (err) {
			recordSentry({ error: err, msg: "Error updating permission " + JSON.stringify(permission) });
		}
	}

	/**
	 * Deletes a permission, syncing the removal with the server or local storage as needed.
	 * Updates the local permissions state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param permission - The permission object to be deleted.
	 * @returns A promise that resolves when the permission is deleted.
	 */
	async function deletePermission(permission: Permission): Promise<void> {
		try {
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				await client.graphql({
					query: deletePerm,
					authMode: "userPool",
					variables: {
						id: permission.id,
					},
				});
				const newPermissions = permissions.filter((perm) => perm.id !== permission.id);
				setPermissions(newPermissions);
			} else {
				await deleteLocalPerm(permission.id);
			}
		} catch (err) {
			recordSentry({ error: err, msg: "Error deleting permission " + JSON.stringify(permission) });
		}
	}

	return {
		createPermission,
		deletePermission,
		updatePermission,
		permissions,
	};
}

export default usePermission;
